import { useState } from 'react';
import { Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { sendWelcomeEmailTest } from '~entities/import-job/api/import-job-api';
import { Link } from '@tanstack/react-router';

import { sanitizeHtml } from '~features/email-editor/utils/sanitizeHtml';

import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Label } from '~shared/shadcn/ui/label';
import { Switch } from '~shared/shadcn/ui/switch';
import { Input } from '~shared/shadcn/ui/input';

interface Step4WelcomeProps {
  welcomeEmailEnabled: boolean;
  onWelcomeEmailEnabledChange: (v: boolean) => void;
  emailSubject: string;
  emailParagraph1: string;
  emailParagraph2: string;
  onEmailSubjectChange: (v: string) => void;
  testParentFirstName?: string;
  testParentLastName?: string;
  onSaveEmailTemplate: () => Promise<void>;
  onNext: () => void | Promise<void>;
  onBack: () => void;
}

type MessagePreview = {
  title: string;
  html: string;
} | null;

function MessagePreviewButton({
  label,
  html,
  onPreview,
}: {
  label: string;
  html: string;
  onPreview: () => void;
}) {
  const hasContent = Boolean(html?.trim());

  return (
    <div className='space-y-2'>
      <Label>{label}</Label>
      <Button
        type='button'
        variant='outline'
        className='w-full justify-start gap-2'
        disabled={!hasContent}
        onClick={onPreview}
      >
        <Eye className='size-4' />
        Preview {label.toLowerCase()}
      </Button>
      {!hasContent && (
        <p className='text-xs text-muted-foreground'>
          No content yet. Use the full email editor to add this message.
        </p>
      )}
    </div>
  );
}

export function Step4Welcome({
  welcomeEmailEnabled,
  onWelcomeEmailEnabledChange,
  emailSubject,
  emailParagraph1,
  emailParagraph2,
  onEmailSubjectChange,
  testParentFirstName,
  testParentLastName,
  onSaveEmailTemplate,
  onNext,
  onBack,
}: Step4WelcomeProps) {
  const [sendingTest, setSendingTest] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activePreview, setActivePreview] = useState<MessagePreview>(null);
  const [testRecipientEmail, setTestRecipientEmail] = useState('');

  const openPreview = (next: NonNullable<MessagePreview>) => {
    setActivePreview(next);
    setPreviewOpen(true);
  };

  const handleContinue = async () => {
    setContinuing(true);
    try {
      await onNext();
    } finally {
      setContinuing(false);
    }
  };

  const handleSendTest = async () => {
    const parentEmail = testRecipientEmail.trim();
    if (!parentEmail) {
      toast.error('Test email could not be sent', {
        description: 'Enter an email address for the test send.',
      });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parentEmail)) {
      toast.error('Test email could not be sent', {
        description: 'Enter a valid email address.',
      });
      return;
    }

    setSendingTest(true);
    try {
      // Only persist when body content is present. Saving empty paragraphs can
      // wipe a previously saved template if hydration has not finished yet.
      // When body is empty, Send Test uses the last saved server template (AC5
      // still applies once the user has real content on this step).
      const hasBodyContent = [emailParagraph1, emailParagraph2].some((html) => {
        const plain = (html ?? '')
          .replace(/<p><\/p>/gi, '')
          .replace(/&nbsp;/gi, ' ')
          .replace(/<[^>]*>/g, '')
          .trim();
        return plain.length > 0;
      });
      if (hasBodyContent) {
        try {
          await onSaveEmailTemplate();
        } catch {
          toast.error('Welcome email could not be saved', {
            description: 'Fix the template and try Send Test again.',
          });
          return;
        }
      }

      try {
        const result = await sendWelcomeEmailTest({
          parentEmail,
          parentFirstName: testParentFirstName,
          parentLastName: testParentLastName,
        });
        if (result.success) {
          toast.success('Test email sent', {
            description: `Check ${parentEmail} for the preview.`,
          });
        } else {
          toast.error('Test email could not be sent');
        }
      } catch {
        toast.error('Test email could not be sent');
      }
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Card className='mx-auto max-w-3xl'>
      <CardContent className='space-y-6 p-6'>
        <div>
          <h2 className='text-xl font-semibold'>Your welcome email</h2>
          <p className='mt-2 text-muted-foreground'>
            Off by default so imports stay quiet. Turn on only if you want
            families to get a set-password link right after import.
          </p>
        </div>

        <div className='flex items-center justify-between rounded-lg border p-4'>
          <div>
            <Label htmlFor='welcome-toggle'>
              Send a welcome email to each family after import?
            </Label>
            <p className='text-sm text-muted-foreground'>
              Leave off to keep mass onboarding email manual. You can still
              email families later from Parent Onboarding Email Batch.
            </p>
          </div>
          <Switch
            id='welcome-toggle'
            checked={welcomeEmailEnabled}
            onCheckedChange={onWelcomeEmailEnabledChange}
          />
        </div>

        <div className='space-y-4'>
          <div>
            <Label htmlFor='email-subject'>Subject</Label>
            <Input
              id='email-subject'
              value={emailSubject}
              onChange={(e) => onEmailSubjectChange(e.target.value)}
            />
          </div>
          <MessagePreviewButton
            label='Opening message'
            html={emailParagraph1}
            onPreview={() =>
              openPreview({ title: 'Opening message', html: emailParagraph1 })
            }
          />
          <MessagePreviewButton
            label='Closing message'
            html={emailParagraph2}
            onPreview={() =>
              openPreview({ title: 'Closing message', html: emailParagraph2 })
            }
          />
          <div className='flex flex-wrap items-end gap-2'>
            <div className='min-w-[220px] flex-1'>
              <Label htmlFor='test-recipient-email' className='sr-only'>
                Test recipient email
              </Label>
              <Input
                id='test-recipient-email'
                type='email'
                placeholder='john.doe@example.com'
                value={testRecipientEmail}
                onChange={(e) => setTestRecipientEmail(e.target.value)}
                disabled={sendingTest}
              />
            </div>
            <Button
              type='button'
              variant='outline'
              disabled={sendingTest}
              onClick={() => void handleSendTest()}
            >
              Send Test
            </Button>
            <Button type='button' variant='ghost' asChild>
              <Link
                to='/email-customizer'
                search={{ returnTo: '/import-data', returnStep: 4 }}
              >
                Open full email editor
              </Link>
            </Button>
          </div>
        </div>

        <div className='flex justify-between'>
          <Button variant='outline' onClick={onBack} disabled={continuing}>
            Back
          </Button>
          <Button
            onClick={() => void handleContinue()}
            disabled={continuing || sendingTest}
          >
            {continuing ? (
              <>
                <Loader2 className='mr-2 size-4 animate-spin' />
                Saving…
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </div>
      </CardContent>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>
              {activePreview?.title ?? 'Message preview'}
            </DialogTitle>
            <DialogDescription>
              How this message will look in the welcome email.
            </DialogDescription>
          </DialogHeader>
          <div
            className='prose prose-sm max-w-none rounded-md border bg-background p-4 text-foreground'
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(activePreview?.html ?? ''),
            }}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
}
